import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Camera } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const ROLE_STYLES = {
  admin: "bg-[#E63946]/10 text-[#E63946] border-[#E63946]/30",
  staff: "bg-[#1B3A6B]/10 text-[#1B3A6B] border-[#1B3A6B]/30",
  client: "bg-slate-100 text-slate-600 border-slate-300",
};

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarRef = useRef(null);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingAvatar(true);
    try {
      const { data } = await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser(data);
      toast.success("Profile picture updated.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploadingAvatar(false);
      if (avatarRef.current) avatarRef.current.value = "";
    }
  };

  const saveProfile = async () => {
    if (!name.trim()) { toast.error("Name is required."); return; }
    setSavingProfile(true);
    try {
      const { data } = await api.put("/auth/profile", { name: name.trim(), phone: phone.trim() });
      setUser(data);
      toast.success("Profile updated.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (pw.next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords do not match."); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Password changed successfully.");
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="profile-title">Profile</h1>
        <p className="text-sm text-slate-500 mt-1">Your account details and password.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="profile-card">
        <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
          <div className="relative">
            <Avatar name={user?.name} userId={user?.id} avatarPath={user?.avatar_path} size="h-20 w-20" textSize="text-2xl" testId="profile-avatar" />
            <input ref={avatarRef} type="file" accept="image/*" className="hidden" data-testid="avatar-input" onChange={uploadAvatar} />
            <button
              onClick={() => avatarRef.current?.click()}
              disabled={uploadingAvatar}
              data-testid="avatar-upload-button"
              title="Upload profile picture (square 1:1 works best)"
              className="absolute -bottom-1.5 -right-1.5 h-7 w-7 rounded-full bg-[#E63946] text-white flex items-center justify-center hover:bg-[#C8323D] transition-colors duration-150 disabled:opacity-50"
            >
              {uploadingAvatar ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            </button>
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-900" data-testid="profile-name">{user?.name}</div>
            <div className="text-sm text-slate-500" data-testid="profile-email">{user?.email}</div>
            <span className={`inline-block mt-1 px-2 py-0.5 rounded-sm text-[11px] font-semibold uppercase tracking-wider border ${ROLE_STYLES[user?.role]}`} data-testid="profile-role">{user?.role}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone-input" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Username</Label>
            <Input value={user?.username || ""} disabled className="border-slate-200 rounded-sm bg-slate-50" data-testid="profile-username" />
          </div>
        </div>
        <Button onClick={saveProfile} disabled={savingProfile} data-testid="profile-save-button" className="bg-primary hover:bg-[#152D53] rounded-sm px-6">
          {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="password-card">
        <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Change Password</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Current Password</Label>
            <Input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">New Password</Label>
            <Input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" className="border-slate-300 rounded-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Confirm New Password</Label>
            <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className="border-slate-300 rounded-sm" />
          </div>
        </div>
        <Button onClick={changePassword} disabled={savingPw || !pw.current || !pw.next} data-testid="pw-change-button" variant="outline" className="rounded-sm border-slate-300">
          {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change Password"}
        </Button>
      </div>
    </div>
  );
}
